import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Users, HandHeart, Calendar, Share2, Check, Link2 } from 'lucide-react';
import { getMyCircleAuthors } from '../lib/api';
import { shareCircle, circleShareUrl } from '../lib/share';
import { useAuth } from '../contexts/AuthContext';

/**
 * "Joined Circles" listing — a card grid of every circle the current
 * user has joined. Each card is a link into that owner's Circle Page
 * (view-only variant: no Join/Unjoin button, since the visitor is
 * already a member).
 */

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return '';
  }
};

const CircleCard = ({ author }) => {
  const handle = author.handle ? `@${author.handle}` : null;
  const displayName = author.name || 'Someone';
  const initial = (displayName[0] || '?').toUpperCase();
  const prayerCount = Number(author.prayer_count) || 0;
  const memberCount = Number(author.member_count) || 0;
  const [shareStatus, setShareStatus] = useState(null); // 'shared' | 'copied' | 'error' | null
  // Dedicated Copy-Link status kept separate from `shareStatus` so a
  // clipboard-only click doesn't visually toggle the sibling Share
  // Circle button into a "Link copied" state.
  const [copyLinkStatus, setCopyLinkStatus] = useState(null); // 'copied' | 'error' | null

  const onShareClick = async (e) => {
    // Prevent the wrapping <Link> from navigating away when the user
    // taps the share button — this control needs to keep them on the
    // list so they can share multiple circles in one sitting.
    e.preventDefault();
    e.stopPropagation();
    // Fire the OS share sheet directly — no intermediate prompt.
    const status = await shareCircle({ ...author, user_id: author.author_user_id });
    setShareStatus(status);
    // Auto-clear the confirmation after a moment so the button reverts
    // to its idle icon.
    if (status === 'shared' || status === 'copied') {
      setTimeout(() => setShareStatus(null), 1800);
    }
  };

  // Clipboard-only "Copy link" — mirrors the Circle Page header
  // button (same URL builder, same emerald flash + auto-revert). No
  // OS share sheet, no side effects on the outer <Link>.
  const onCopyLinkClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = circleShareUrl({
      user_id: author.author_user_id,
      handle: author.handle,
      name: author.name,
    });
    if (!url) {
      setCopyLinkStatus('error');
      setTimeout(() => setCopyLinkStatus(null), 1800);
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyLinkStatus('copied');
    } catch (err) {
      console.warn('copy circle link failed', err);
      setCopyLinkStatus('error');
    }
    setTimeout(() => setCopyLinkStatus(null), 1800);
  };

  return (
    <Link
      to={`/circle/${author.author_user_id}`}
      state={{ fromJoinedCircles: true }}
      data-testid={`circle-card-${author.author_user_id}`}
      className="group flex flex-col items-center text-center rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 hover:shadow-lg hover:border-amber-300 dark:hover:border-amber-800/60 hover:-translate-y-0.5 transition-all px-6 pt-8 pb-6"
    >
      {author.picture ? (
        <img
          src={author.picture}
          alt={displayName}
          className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-100 dark:ring-amber-900/50 group-hover:ring-amber-300 dark:group-hover:ring-amber-700 transition-colors"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 text-lg font-medium ring-2 ring-amber-100 dark:ring-amber-900/50 group-hover:ring-amber-300 dark:group-hover:ring-amber-700 transition-colors">
          {initial}
        </div>
      )}
      <h3
        className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white truncate max-w-full"
        data-testid={`circle-card-name-${author.author_user_id}`}
      >
        {displayName}
      </h3>
      {handle && (
        <div
          className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400 truncate max-w-full"
          data-testid={`circle-card-handle-${author.author_user_id}`}
        >
          {handle}
        </div>
      )}
      <div className="mt-4 flex items-center justify-center gap-4 text-xs text-zinc-500 dark:text-zinc-400">
        <span
          className="inline-flex items-center gap-1"
          data-testid={`circle-card-created-${author.author_user_id}`}
        >
          <Calendar size={12} className="text-zinc-400" />
          Joined {fmtDate(author.author_created_at) || '—'}
        </span>
        <span className="text-zinc-300 dark:text-zinc-700">•</span>
        <span
          className="inline-flex items-center gap-1"
          data-testid={`circle-card-member-count-${author.author_user_id}`}
        >
          <Users size={12} className="text-purple-500" />
          {memberCount} {memberCount === 1 ? 'member' : 'members'}
        </span>
        <span className="text-zinc-300 dark:text-zinc-700">•</span>
        <span
          className="inline-flex items-center gap-1"
          data-testid={`circle-card-prayer-count-${author.author_user_id}`}
        >
          <HandHeart size={12} className="text-amber-500" />
          {prayerCount} {prayerCount === 1 ? 'prayer' : 'prayers'}
        </span>
      </div>
      <div className="mt-4 flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={onShareClick}
          data-testid={`circle-card-share-btn-${author.author_user_id}`}
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border transition-colors ${
            shareStatus === 'shared' || shareStatus === 'copied'
              ? 'bg-emerald-500 border-emerald-500 text-white'
              : 'border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'
          }`}
        >
          {shareStatus === 'shared' && <><Check size={12} /> Shared</>}
          {shareStatus === 'copied' && <><Check size={12} /> Copied</>}
          {shareStatus === 'error' && <><Share2 size={12} /> Try again</>}
          {!shareStatus && <><Share2 size={12} /> Share Circle</>}
        </button>
        <button
          type="button"
          onClick={onCopyLinkClick}
          data-testid={`circle-card-copy-link-btn-${author.author_user_id}`}
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border transition-colors ${
            copyLinkStatus === 'copied'
              ? 'bg-emerald-500 border-emerald-500 text-white'
              : 'border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'
          }`}
        >
          {copyLinkStatus === 'copied' && <><Check size={12} /> Circle link copied</>}
          {copyLinkStatus === 'error' && <><Link2 size={12} /> Try again</>}
          {!copyLinkStatus && <><Link2 size={12} /> Copy link</>}
        </button>
      </div>
    </Link>
  );
};

const Circles = () => {
  const { user, loading: authLoading, openAuthModal } = useAuth();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await getMyCircleAuthors();
      setAuthors(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('[Circles] load failed', e);
      setError('Failed to load your circles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    load();
  }, [user, authLoading, load]);

  if (!authLoading && !user) {
    return (
      <div className="px-4 sm:px-6 py-16 text-center max-w-md mx-auto">
        <Users size={40} className="mx-auto text-amber-500 mb-3" />
        <div className="text-lg font-semibold mb-1">Sign in to see your circles</div>
        <div className="text-sm text-zinc-500 mb-5">
          Join circles to keep up with prayers from people you follow.
        </div>
        <button
          type="button"
          onClick={openAuthModal}
          data-testid="circles-signin-btn"
          className="h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold"
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div data-testid="circles-page">
      {/* Header — mirrors My Lit Candles / My Prayers so the sidebar
          sibling pages share a unified visual language. */}
      <div className="px-4 sm:px-6 pt-6 pb-4 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600">
            <Users size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold" data-testid="circles-title">Joined Circles</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
              Every circle you&apos;ve joined, most recently joined first.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 py-6">
        {loading && (
          <div className="py-16 flex items-center justify-center gap-2 text-zinc-500" data-testid="circles-loading">
            <Loader2 className="animate-spin" size={16} /> Loading your circles…
          </div>
        )}

        {!loading && error && (
          <div className="py-16 text-center text-red-600 dark:text-red-400 text-sm" data-testid="circles-error">
            {error}
          </div>
        )}

        {!loading && !error && authors.length === 0 && (
          <div
            className="mt-6 mx-auto max-w-lg rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/60 shadow-sm px-8 py-10 flex flex-col items-center text-center"
            data-testid="circles-empty"
          >
            <div
              aria-hidden="true"
              className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 dark:text-amber-300 mb-4"
            >
              <Users size={26} strokeWidth={2.25} />
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
              No circles joined yet
            </h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed max-w-sm">
              Tap <span className="font-medium">Join Circle</span> on any member&apos;s prayer to follow them.
            </p>
            <div className="mt-5">
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 text-sm font-medium"
              >
                Explore prayers
              </Link>
            </div>
          </div>
        )}

        {!loading && !error && authors.length > 0 && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-x-4 gap-y-8"
            data-testid="circles-grid"
          >
            {authors.map((a) => (
              <CircleCard key={a.author_user_id} author={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Circles;
